import {
    type ReactNode,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITimelineProps from "./type";
import type {
    TimelinePositionContextType,
    TimelineContextType,
    TimelineGroupType
} from "./type";
import stylesheet, {
    getTimelineMeasures,
    useStyles
} from "./stylesheet";
import TimelineActions from "./components/actions";
import TimelineBody from "./components/body";
import TimelineChange from "./components/change";
import TimelineDay from "./components/day";
import TimelineEmphasis from "./components/emphasis";
import TimelineItem from "./components/item";
import TimelineLink from "./components/link";
import TimelineLoadMore from "./components/loadMore";
import {
    TimelinePositionContext,
    TimelineContext
} from "./context";
import TimelineSkeletonItem from "./internal/skeletonItem";
import TimelineState from "./internal/state";
import {
    TIMELINE_TICK_DURATION,
    getTimelineDayOffset,
    getTimelineGroups
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    Calendar as CalendarIcon,
    Lock as LockIcon
} from "lucide-react-native";
import Skeleton from "../skeleton";
import Alert from "../alert";

const TIMELINE_SKELETON_COUNT = 3;

const TimelinePositionProvider: FC<TimelinePositionContextType & {
    children: ReactNode;
}> = ({
    isDayGrouped,
    feedCount,
    feedIndex,
    children,
    isFinal,
    isToday,
    isLast
}) => {
    const value = useMemo(() => ({
        isDayGrouped,
        feedCount,
        feedIndex,
        isFinal,
        isToday,
        isLast
    }), [
        isDayGrouped,
        feedCount,
        feedIndex,
        isFinal,
        isToday,
        isLast
    ]);

    return <TimelinePositionContext.Provider
        value={value}
    >
        {children}
    </TimelinePositionContext.Provider>;
};

const Timeline: FC<ITimelineProps> = ({
    isPermissionDenied = false,
    "aria-label": ariaLabel,
    isGroupedByDay = false,
    timeBehaviour = "auto",
    isLoadingMore = false,
    isEndReached = false,
    accessibilityLabel,
    isLoading = false,
    onRequestAccess,
    size = "medium",
    customLocalize,
    emptyContent,
    error = null,
    customTheme,
    onLoadMore,
    totalCount,
    children,
    onRetry,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        avatarSizes,
        typography,
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        now,
        setNow
    ] = useState(() => Date.now());

    useEffect(() => {
        if(timeBehaviour === "absolute") {
            return;
        }

        const timerID = setInterval(() => {
            setNow(Date.now());
        }, TIMELINE_TICK_DURATION);

        return () => {
            clearInterval(timerID);
        };
    }, [
        timeBehaviour
    ]);

    const groups = useMemo(() => getTimelineGroups({
        isGrouped: isGroupedByDay,
        children
    }), [
        isGroupedByDay,
        children
    ]);

    const itemCount = groups.reduce((total, group) => total + group.items.length, 0);

    const isFeed = !!onLoadMore && !isGroupedByDay;

    const hasMore = !!onLoadMore && !isEndReached;

    const currentLabel = ariaLabel ?? accessibilityLabel ?? localize("timeline-label");

    const measures = getTimelineMeasures({
        shapeConfigs,
        avatarSizes,
        typography,
        iconSizes,
        spaces,
        size
    });

    const {
        container: containerDynamicStyle,
        group: groupDynamicStyle
    } = useStyles({
        gap: measures.gap,
        spaces
    });

    const contextValue = useMemo((): TimelineContextType => ({
        customLocalize,
        isLoadingMore,
        timeBehaviour,
        activeLocale,
        isEndReached,
        customTheme,
        onLoadMore,
        totalCount,
        itemCount,
        hasMore,
        isFeed,
        size,
        now
    }), [
        customLocalize,
        isLoadingMore,
        timeBehaviour,
        activeLocale,
        isEndReached,
        customTheme,
        onLoadMore,
        totalCount,
        itemCount,
        hasMore,
        isFeed,
        size,
        now
    ]);

    const renderGroup = (group: TimelineGroupType, groupIndex: number, offset: number) => {
        const isToday = !!group.date && getTimelineDayOffset({
            date: group.date,
            now
        }) === 0;

        const isLastGroup = groupIndex === groups.length - 1;

        return <View
            style={[
                stylesheet.list,
                isGroupedByDay && !isLastGroup ? groupDynamicStyle : undefined
            ]}
            key={group.key}
        >
            {
                isGroupedByDay && group.date ?
                    <TimelineDay
                        date={group.date}
                    />
                :
                    null
            }
            <View
                aria-label={isGroupedByDay ? undefined : currentLabel}
                aria-busy={isFeed ? isLoadingMore : undefined}
                role={isFeed ? "feed" : "list"}
                style={stylesheet.list}
            >
                {group.items.map((item, itemIndex) => <TimelinePositionProvider
                    isFinal={isLastGroup && itemIndex === group.items.length - 1}
                    isLast={itemIndex === group.items.length - 1}
                    key={item.key ?? `${itemIndex}`}
                    feedIndex={offset + itemIndex}
                    isDayGrouped={isGroupedByDay}
                    feedCount={totalCount ?? -1}
                    isToday={isToday}
                >
                    {item}
                </TimelinePositionProvider>)}
            </View>
        </View>;
    };

    const renderGroups = () => {
        let offset = 0;

        return groups.map((group, groupIndex) => {
            const currentOffset = offset;

            offset += group.items.length;

            return renderGroup(group, groupIndex, currentOffset);
        });
    };

    const renderSkeleton = () => {
        return <View
            style={stylesheet.list}
        >
            {Array.from({
                length: TIMELINE_SKELETON_COUNT
            }, (_, index) => <TimelineSkeletonItem
                isLast={index === TIMELINE_SKELETON_COUNT - 1}
                customTheme={customTheme}
                index={index}
                key={index}
                size={size}
            />)}
        </View>;
    };

    const renderEmpty = () => {
        if(emptyContent) {
            return emptyContent;
        }

        return <TimelineState
            description={localize("timeline-empty-desc")}
            title={localize("timeline-empty-title")}
            customTheme={customTheme}
            icon={CalendarIcon}
        />;
    };

    const renderError = () => {
        if(!error) {
            return null;
        }

        return <Alert
            actions={onRetry ? [
                {
                    title: localize("try-again"),
                    onPress: onRetry
                }
            ] : undefined}
            customLocalize={customLocalize}
            customTheme={customTheme}
            type="danger"
            title={error}
        />;
    };

    const renderBody = () => {
        if(isPermissionDenied) {
            return <TimelineState
                description={localize("timeline-no-permission-desc")}
                actionTitle={localize("timeline-request-access")}
                title={localize("timeline-no-permission-title")}
                onActionPress={onRequestAccess}
                customTheme={customTheme}
                icon={LockIcon}
            />;
        }

        return <Fragment>
            <Skeleton.Group
                loadingLabel={localize("timeline-loading")}
                customLocalize={customLocalize}
                placeholder={renderSkeleton()}
                customTheme={customTheme}
                isLoading={isLoading}
            >
                {itemCount || error ? renderGroups() : renderEmpty()}
            </Skeleton.Group>
            {isLoading ? null : renderError()}
            {
                onLoadMore && !isLoading && itemCount ?
                    <TimelineLoadMore />
                :
                    null
            }
        </Fragment>;
    };

    return <TimelineContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            aria-label={isGroupedByDay ? currentLabel : undefined}
            role={isGroupedByDay ? "group" : undefined}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {renderBody()}
        </View>
    </TimelineContext.Provider>;
};
export default Object.assign(Timeline, {
    Emphasis: TimelineEmphasis,
    LoadMore: TimelineLoadMore,
    Actions: TimelineActions,
    Change: TimelineChange,
    Body: TimelineBody,
    Item: TimelineItem,
    Link: TimelineLink,
    Day: TimelineDay
});
